import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiEye,
  FiEyeOff,
  FiMail,
  FiLock,
  FiUser,
  FiPhone,
  FiRefreshCw,
  FiCopy,
} from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";

const RegisterContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 2rem 1rem;
`;

const RegisterCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  width: 100%;
  max-width: 500px;
`;

const RegisterHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-left: 2.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
    box-shadow: 0 0 0 3px rgba(45, 80, 22, 0.1);
  }

  &.error {
    border-color: #dc3545;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 1.125rem;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  font-size: 1.125rem;
  padding: 0.25rem;

  &:hover {
    color: #333;
  }
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  background: #2d5016;
  color: white;
  border: none;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #1a3a0e;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TermsText = styled.p`
  font-size: 0.75rem;
  color: #666;
  text-align: center;
  line-height: 1.4;

  a {
    color: #2d5016;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 1.5rem 0;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e0e0e0;
  }

  span {
    padding: 0 1rem;
    color: #666;
    font-size: 0.875rem;
  }
`;

const GoogleButton = styled.button`
  width: 100%;
  padding: 0.875rem 1rem;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  color: #333;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;

  &:hover {
    border-color: #4285f4;
    box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const GoogleIcon = styled.div`
  width: 20px;
  height: 20px;
  background: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIyLjU2IDEyLjI1QzIyLjU2IDExLjQ3IDIyLjQ5IDEwLjcyIDIyLjM2IDEwSDEyVjE0LjMzSDE3Ljk2QzE3LjY2IDE1Ljk4IDE2Ljg4IDE3LjM4IDE1Ljc4IDE4LjM5VjIwLjk2SDE5Ljg4QzIxLjUzIDE5LjM0IDIyLjU2IDE3LjA2IDIyLjU2IDEyLjI1WiIgZmlsbD0iIzQyODVGNCIvPgo8cGF0aCBkPSJNMTIgMjNDMTQuOTcgMjMgMTcuNjQgMjEuOTkgMTkuODggMjAuOTZMMTUuNzggMTguMzlDMTQuNzggMTkuMDEgMTMuNTEgMTkuMzMgMTIgMTkuMzNDOS4xNiAxOS4zMyA2Ljg1IDE3LjU3IDYuMDUgMTUuMDdMNC4wOSAxNi41NkM2LjI2IDIwLjg2IDEwLjA2IDIzIDEyIDIzWiIgZmlsbD0iIzM0QTg1MyIvPgo8cGF0aCBkPSJNNi4wNSA5LjA3VjE1LjA3TDEyIDE5LjMzQzE0Ljk3IDE5LjMzIDE3LjY0IDE4LjMyIDE5Ljg4IDE2Ljc5VjIwLjk2SDEyQzEwLjA2IDIzIDYuMjYgMjAuODYgNC4wOSAxNi41Nkw2LjA1IDE1LjA3QzYuODUgMTcuNTcgOS4xNiAxOS4zMyAxMiAxOS4zM1Y5LjA3SDYuMDVaIiBmaWxsPSIjRkJCQzA0Ii8+CjxwYXRoIGQ9Ik0xMiA0LjY3QzEzLjUxIDQuNjcgMTQuNzggNS4wMSAxNS43OCA1LjYzTDE5Ljg4IDMuMDZMMTUuNzggMS4wNEMxMy41MSAwLjAxIDEwLjk3IDAgMTIgMEMxMC4wNiAwIDYuMjYgMi4xNCA0LjA5IDUuNDRMNi4wNSA2LjkzQzYuODUgNC40MyA5LjE2IDIuNjcgMTIgMi42N1Y0LjY3WiIgZmlsbD0iI0VCMzMzMyIvPgo8L3N2Zz4K")
    no-repeat center;
  background-size: contain;
`;

const PasswordSuggestion = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.5rem;
`;

const SuggestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const SuggestionTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #2d5016;
`;

const SuggestionActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SuggestionButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;

  &:hover {
    background: #e9ecef;
    color: #2d5016;
  }
`;

const SuggestedPassword = styled.div`
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
  color: #333;
  background: white;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  word-break: break-all;
  margin-bottom: 0.5rem;
`;

const PasswordStrength = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

const StrengthBar = styled.div`
  height: 4px;
  border-radius: 2px;
  flex: 1;
  background: #e0e0e0;
  transition: background-color 0.3s ease;

  &.weak {
    background: #dc3545;
  }
  &.fair {
    background: #ffc107;
  }
  &.good {
    background: #17a2b8;
  }
  &.strong {
    background: #28a745;
  }
`;

const StrengthText = styled.span`
  font-size: 0.75rem;
  color: #666;
`;

const RememberMeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #2d5016;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #666;
  cursor: pointer;
  user-select: none;
`;

const SignInLink = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e0e0e0;

  p {
    color: #666;
    font-size: 0.875rem;
    margin-bottom: 0.5rem;
  }

  a {
    color: #2d5016;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [suggestedPassword, setSuggestedPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  const { register: registerUser, loginWithGoogle, loading } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm();

  const password = watch("password");

  // Password generation function
  const generatePassword = () => {
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";

    let password = "";

    // Ensure at least one character from each category
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];

    // Fill the rest randomly
    const allChars = lowercase + uppercase + numbers + symbols;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Shuffle the password
    return password
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  };

  // Password strength calculation
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: "", bars: 0 };

    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (pwd.length >= 12) score += 1;
    if (/[a-z]/.test(pwd)) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (score <= 2)
      return { strength: score, label: "Weak", bars: 1, class: "weak" };
    if (score <= 3)
      return { strength: score, label: "Fair", bars: 2, class: "fair" };
    if (score <= 4)
      return { strength: score, label: "Good", bars: 3, class: "good" };
    return { strength: score, label: "Strong", bars: 4, class: "strong" };
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setSuggestedPassword(newPassword);
    setShowSuggestion(true);
  };

  const handleUseSuggestedPassword = () => {
    // Set the suggested password in the form
    const passwordInput = document.getElementById("password");
    const confirmPasswordInput = document.getElementById("confirmPassword");

    if (passwordInput) passwordInput.value = suggestedPassword;
    if (confirmPasswordInput) confirmPasswordInput.value = suggestedPassword;

    // Trigger form validation
    passwordInput?.dispatchEvent(new Event("input", { bubbles: true }));
    confirmPasswordInput?.dispatchEvent(new Event("input", { bubbles: true }));

    setShowSuggestion(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(suggestedPassword);
      // You could add a toast notification here
    } catch (err) {
      console.error("Failed to copy password:", err);
    }
  };

  const onSubmit = async (data) => {
    const { confirmPassword, ...userData } = data;
    const result = await registerUser(userData, rememberMe);
    if (result.success) {
      navigate("/");
    } else {
      setError("root", {
        type: "manual",
        message: result.error,
      });
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <RegisterHeader>
          <h1>Join NourishGY</h1>
          <p>Create your account to start enjoying authentic Guyanese meals</p>
        </RegisterHeader>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormRow>
            <FormGroup>
              <Label htmlFor="firstName">First Name</Label>
              <InputContainer>
                <InputIcon>
                  <FiUser />
                </InputIcon>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="First name"
                  className={errors.firstName ? "error" : ""}
                  {...register("firstName", {
                    required: "First name is required",
                    minLength: {
                      value: 2,
                      message: "First name must be at least 2 characters",
                    },
                  })}
                />
              </InputContainer>
              {errors.firstName && (
                <ErrorMessage>{errors.firstName.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="lastName">Last Name</Label>
              <InputContainer>
                <InputIcon>
                  <FiUser />
                </InputIcon>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="Last name"
                  className={errors.lastName ? "error" : ""}
                  {...register("lastName", {
                    required: "Last name is required",
                    minLength: {
                      value: 2,
                      message: "Last name must be at least 2 characters",
                    },
                  })}
                />
              </InputContainer>
              {errors.lastName && (
                <ErrorMessage>{errors.lastName.message}</ErrorMessage>
              )}
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <InputContainer>
              <InputIcon>
                <FiMail />
              </InputIcon>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                className={errors.email ? "error" : ""}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
              />
            </InputContainer>
            {errors.email && (
              <ErrorMessage>{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">Phone Number (Optional)</Label>
            <InputContainer>
              <InputIcon>
                <FiPhone />
              </InputIcon>
              <Input
                id="phone"
                type="tel"
                placeholder="Enter your phone number"
                className={errors.phone ? "error" : ""}
                {...register("phone", {
                  pattern: {
                    value: /^[\+]?[1-9][\d]{0,15}$/,
                    message: "Invalid phone number",
                  },
                })}
              />
            </InputContainer>
            {errors.phone && (
              <ErrorMessage>{errors.phone.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <InputContainer>
              <InputIcon>
                <FiLock />
              </InputIcon>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                className={errors.password ? "error" : ""}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </PasswordToggle>
            </InputContainer>
            {errors.password && (
              <ErrorMessage>{errors.password.message}</ErrorMessage>
            )}

            {/* Password Suggestion Feature */}
            <PasswordSuggestion>
              <SuggestionHeader>
                <SuggestionTitle>💡 Password Helper</SuggestionTitle>
                <SuggestionActions>
                  <SuggestionButton onClick={handleGeneratePassword}>
                    <FiRefreshCw />
                    Generate
                  </SuggestionButton>
                </SuggestionActions>
              </SuggestionHeader>

              {showSuggestion && suggestedPassword && (
                <>
                  <SuggestedPassword>{suggestedPassword}</SuggestedPassword>
                  <PasswordStrength>
                    {[1, 2, 3, 4].map((bar) => (
                      <StrengthBar
                        key={bar}
                        className={
                          bar <= getPasswordStrength(suggestedPassword).bars
                            ? getPasswordStrength(suggestedPassword).class
                            : ""
                        }
                      />
                    ))}
                  </PasswordStrength>
                  <StrengthText>
                    Strength: {getPasswordStrength(suggestedPassword).label}
                  </StrengthText>
                  <SuggestionActions>
                    <SuggestionButton onClick={handleUseSuggestedPassword}>
                      Use This Password
                    </SuggestionButton>
                    <SuggestionButton onClick={handleCopyPassword}>
                      <FiCopy />
                      Copy
                    </SuggestionButton>
                  </SuggestionActions>
                </>
              )}
            </PasswordSuggestion>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <InputContainer>
              <InputIcon>
                <FiLock />
              </InputIcon>
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                className={errors.confirmPassword ? "error" : ""}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === password || "Passwords do not match",
                })}
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </PasswordToggle>
            </InputContainer>
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
            )}
          </FormGroup>

          <RememberMeContainer>
            <Checkbox
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <CheckboxLabel htmlFor="rememberMe">
              Remember me for 30 days
            </CheckboxLabel>
          </RememberMeContainer>

          {errors.root && <ErrorMessage>{errors.root.message}</ErrorMessage>}

          <SubmitButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <div className="loading-spinner" />
                Creating Account...
              </>
            ) : (
              "Create Account"
            )}
          </SubmitButton>

          <Divider>
            <span>or</span>
          </Divider>

          <GoogleButton type="button" onClick={loginWithGoogle}>
            <GoogleIcon />
            Continue with Google
          </GoogleButton>

          <TermsText>
            By creating an account, you agree to our{" "}
            <Link to="/terms">Terms of Service</Link> and{" "}
            <Link to="/privacy">Privacy Policy</Link>
          </TermsText>
        </Form>

        <SignInLink>
          <p>Already have an account?</p>
          <Link to="/login">Sign in to NourishGY</Link>
        </SignInLink>
      </RegisterCard>
    </RegisterContainer>
  );
};

export default Register;
